import store from '@/store'
import router from '@/router'
import serverErrorAlert from '@/utils/serverErrorAlert'

// Types whose alert is owned by whoever made the request, because they know which field is at
// fault and what the guest should do next -- a page component for the guest/checkin types, or
// `errorDispatcher` from a store action for the booking ones. The interceptor must stay quiet for
// all of them: `setAlert` has a single slot, so two dispatchers racing over it means the second
// silently destroys the first -- which is how the real reason for ENG-2910 was thrown away on
// every occurrence.
//
// The Booking* types are the subtle half. `bookings.ts` already renders them in full through
// errorDispatcher, with interpolation and a navigation action; alerting here as well produced a
// second, worse alert from the same locale keys -- `messages.<Type>[0]` only, and with no params,
// so BookingNotAllowedToCheckinYet rendered a literal `{formattedDate}`. It lost the race today,
// but only by luck of ordering.
const CALLER_HANDLED_TYPES = [
  'GuestNotFound',
  'CheckinGuestMismatch',
  'GuestEmailReusedOnOverlappingBooking',
  'MustVerifyDateOfBirthToAssignGuest',
  'MustVerifyDateOfBirthToReassignGuest',
  'IncorrectDateOfBirthWhenAssigningGuest',
  'IncorrectDateOfBirthWhenReassigningGuest',
  'BookingNotFound',
  'BookingNotActive',
  'BookingNotValidForCheckin',
  'BookingNotAllowedToCheckinYet',
  'BookingStatusNotValidForCheckin',
  // The missing-surname 422 (section-l/api#1400): fetchBooking asks for the surname again. ENG-3129.
  'LastNameRequired',
]

const ErrorHandler = class {
  handle(e) {
    const error = e?.response?.data || e?.response || e

    if (this.isTransportFailure(e)) {
      // Give transport failures a type of their own. Without one, `error?.type` is undefined and a
      // lost Wi-Fi connection is indistinguishable from a server rejection at every call site.
      const transportError = {
        type: 'NetworkError',
        transport: true,
      }
      serverErrorAlert(transportError)
      return Promise.reject(transportError)
    }

    if (error.type === 'InvalidPropCode') {
      this.invalidPropCode()
    } else if (CALLER_HANDLED_TYPES.includes(error.type)) {
      // Alerted by whoever made the request -- see CALLER_HANDLED_TYPES.
    } else if (store.getters['auth/loggedIn'] && e?.response?.status === 401) {
      this.unauthorizedError()
    } else if (error.message || error.messages) {
      serverErrorAlert(error)
    }

    return Promise.reject(error)
  }

  // No response at all means the request never got an answer: offline, DNS, CORS, or the 15s
  // timeout in request.ts, which arrives as ECONNABORTED rather than the "Network Error" message
  // the old check looked for.
  isTransportFailure(e) {
    if (e?.response) return false

    return (
      e?.code === 'ECONNABORTED' ||
      e?.code === 'ERR_NETWORK' ||
      e?.message === 'Network Error' ||
      e?.request != null
    )
  }

  unauthorizedError() {
    router.push({ name: 'Logout' })
  }

  invalidPropCode() {
    const property = store.getters['properties/property']
    store.dispatch('setAlert', {
      title: `Oops. Are you still at ${property.name}?`,
      messages: [
        'It seems your session has expired. We need to make sure you are still at the property.',
        `Please scan the QR code on the wall to confirm you are at ${property.name}.`,
      ],
      buttonText: 'Scan QR code',
      buttonAction: async () => {
        await store.dispatch('resetStore')
        router.push({ name: 'Scan', params: { resource: 'property' } })
      },
    })
    store.dispatch('properties/resetProperty')
  }
}

export default new ErrorHandler()
